<template>
  <div class="inputWrapper">
    <input
      :name="name"
      :id="id"
      class="input"
      :class="[validityClass, ...extraClasses]"
      :placeholder="placeholder"
      :type="typeProxy"
      :required="required"
      :pattern="pattern"
      :disabled="disabled"
      :value="value"
      :minlength="minlength"
      @input="updateValue($event.target.value)"
    />
    <div v-if="type === 'password'" @click.prevent="togglePassword">
      <div v-if="showPassword">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" class="password-toggle">
          <path d="M320 400c-75.85 0-137.25-58.71-142.9-133.11L72.2 185.82c-13.79 17.3-26.48
          35.59-36.72 55.59a32.35 32.35 0 0 0 0 29.19C89.71 376.41 197.07 448 320 448c26.91 0
          52.87-4 77.89-10.46L346 397.39a144.13 144.13 0 0 1-26 2.61zm313.82
          58.1l-110.55-85.44a331.25 331.25 0 0 0 81.25-102.07 32.35 32.35 0 0 0 0-29.19C550.29
          135.59 442.93 64 320 64a308.15 308.15 0 0 0-147.32 37.7L45.46 3.37A16 16 0 0 0 23
          6.18L3.37 31.45A16 16 0 0 0 6.18 53.9l588.36 454.73a16 16 0 0 0
          22.46-2.81l19.64-25.27a16 16 0 0 0-2.82-22.45zm-183.72-142l-39.3-30.38A94.75 94.75
          0 0 0 416 256a94.76 94.76 0 0 0-121.31-92.21A47.65 47.65 0 0 1 304 192a46.64 46.64
          0 0 1-1.54 10l-73.61-56.89A142.31 142.31 0 0 1 320 112a143.92 143.92 0 0 1 144 144c0
          21.63-5.29 41.79-13.9 60.11z"/>
        </svg>
      </div>
      <div v-else>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" class="password-toggle" fill="red">
          <path d="M572.52 241.4C518.29 135.59 410.93 64 288 64S57.68 135.64 3.48 241.41a32.35
          32.35 0 0 0 0 29.19C57.71 376.41 165.07 448 288 448s230.32-71.64 284.52-177.41a32.35
          32.35 0 0 0 0-29.19zM288 400a144 144 0 1 1 144-144 143.93 143.93 0 0 1-144
          144zm0-240a95.31 95.31 0 0 0-25.31 3.79 47.85 47.85 0 0 1-66.9 66.9A95.78 95.78 0 1
          0 288 160z"/>
        </svg>
      </div>
    </div>
    <div v-if="type ==='search'">
      <svg xmlns="http://www.w3.org/2000/svg" class="search-icon" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817
        4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
      </svg>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Input',
  props: {
    value: {
      type: String,
      required: false,
    },
    type: {
      type: String,
      default: 'text',
    },
    name: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      required: false,
    },
    placeholder: {
      type: String,
      required: false,
    },
    size: {
      type: String,
      default: 'md',
    },
    block: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    pattern: {
      type: String,
      required: false,
    },
    minlength: {
      type: String,
      required: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showPassword: false,
    };
  },
  computed: {
    validityClass() {
      return this.invalid ? 'invalid' : '';
    },
    extraClasses() {
      return [
        this.block ? 'input--block' : '',
        `input--${this.size}`,
        `input--${this.type}`,
      ];
    },
    typeProxy() {
      if (this.type === 'password') {
        return this.showPassword ? 'text' : 'password';
      }
      return this.type;
    },
  },
  methods: {
    togglePassword() {
      this.showPassword = !this.showPassword;
    },
    updateValue(value) {
      this.$emit('input', value);
    },
  },
};
</script>

<style scoped>
.inputWrapper {
  position: relative;
}

.input {
  z-index: 1;
  background-color: var(--color-white, #ffffff);
  color: var(--color-text, #1a1a1a);
  border: 1px solid var(--border-color, #d1d1d1);
  border-radius: var(--border-radius-regular, 4px);
  transition: all 120ms cubic-bezier(0.37, 0, 0.63, 1);
}

.input[type="search"] {
    -webkit-appearance: textfield;
}

.input[type="search"]::-webkit-search-decoration,
.input[type="search"]::-webkit-search-cancel-button,
.input[type="search"]::-webkit-search-results-button,
.input[type="search"]::-webkit-search-results-decoration  {
    -webkit-appearance: none;
}

.input:focus,
.input:-webkit-autofill:focus {
  border-color: var(--border-color-focus, #767676);
  outline: none;
  /* First shadow is a hack to prevent the automcomplete styling in chrome */
  box-shadow:
    inset 0 0 0 3rem var(--color-white, #ffffff),
    0 0 0 var(--size-1-4, 4px) var(--color-richblack-10, #d1d1d1);
}

.input:not(:placeholder-shown):valid,
.input:not(:placeholder-shown):valid:focus {
  border-color: var(--color-success, #46ad20);
  /* First shadow is a hack to prevent the automcomplete styling in chrome */
  box-shadow:
    inset 0 0 0 3rem var(--color-white, #ffffff),
    0 0 0 var(--size-1-4, 4px) var(--color-success-lightest, #b7dea5);
}

.input.invalid,
.input:valid.invalid,
.input.invalid:-webkit-autofill,
.input:not(:placeholder-shown):invalid {
  border-color: var(--color-error, #ff381e);
  /* First shadow is a hack to prevent the automcomplete styling in chrome */
  box-shadow:
    inset 0 0 0 3rem var(--color-white, #ffffff),
    0 0 0 var(--size-1-4, 4px) var(--color-error-lightest, #ffb0a8);
}

.input[disabled] {
  opacity: .64;
  pointer-events: none;
}

/* Inputs sizes */
.input--block {
  display: block;
  width: 100%;
}
.input--md {
  font-size: var(--text-md, 16px);
  padding: var(--size-1-2, 8px) var(--size-1, 16px);
}
.input--lg {
  font-size: var(--text-md);
  padding: calc(var(--unit-base, 16px) * 0.875) var(--size-2, 32px);
}

.password-toggle {
  position: absolute;
  top: 50%;
  right: var(--size-1-2, 8px);
  width: calc(var(--unit-base, 16px) * 1.25);
  height: calc(var(--unit-base, 16px) * 1.25);
  transform: translateY(-50%);
  fill: var(--color-text-muted, #a3a3a3);
}
.password-toggle:hover {
  fill: var(--color-text, #484848);
  cursor: pointer;
}

.search-icon {
  position: absolute;
  top: 50%;
  right: var(--size-1-2, 8px);
  width: calc(var(--unit-base, 16px) * 1.25);
  height: calc(var(--unit-base, 16px) * 1.25);
  transform: translateY(-50%);
  fill: var(--color-text-muted, #a3a3a3);
}
</style>
